import { PaginatedTable } from '@/components/seisveinte/Table/PaginatedTable'
import { TableHeaderCellOptions } from '@/components/seisveinte/Table/table.types'
import {
  downloadFileFromProcess,
  downloadProcessOutput,
  getEnrichmentTypes,
  getProcessesPaginated,
  getProcessOutputs,
  getProcessStates,
} from '@/lib/processQueries'
import { ColumnDef } from '@tanstack/table-core'
import TableHeader from './TableHeader'
import { useState, useEffect, useRef } from 'react'
import { useQuery, useLazyQuery } from '@apollo/client'
import Download from '@/assets/Download'
import ViewIcon from '@/assets/ViewIcon'
import EditIcon from '@/assets/EditIcon'
import { format } from 'date-fns'
import { abstractTypeToOptions, download64File } from '@/lib/utils'
import { PROCESS_STATES } from '@/lib/constants'
import Tooltip from '@/components/compliance-assistant/Tooltip'
import StateIndicator from '@/components/compliance-assistant/StateIndicator'
import { useNavigate } from 'react-router-dom'
import { useToastNotification } from '@/components/compliance-assistant/ToastNotification'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ProcessColumns {
  state: string
  clientName: string
  name: string
  enrichmentType: string
  createdAt: string
  uuid: string
}

interface ProcessOutputData {
  id: number
  naturalKey: string
  name: string
}

export default function HistoricProcesses() {
  const { triggerToast } = useToastNotification()
  const navigate = useNavigate()
  const [extraFilters, setExtraFilters] = useState({})
  
  const { data: processStates } = useQuery(getProcessStates)
  const { data: enrichmentTypes } = useQuery(getEnrichmentTypes)
  
  const stateOptions = abstractTypeToOptions(processStates?.getProcessStates || [])
  const enrichmentTypeOptions = abstractTypeToOptions(
    enrichmentTypes?.getEnrichmentsTypes.enrichmentTypes || []
  )

  const [downloadFileRequest] = useLazyQuery(downloadFileFromProcess)
  const [downloadOutputRequest] = useLazyQuery(downloadProcessOutput)
  const [fetchProcessOutputs] = useLazyQuery(getProcessOutputs)

  // Mapa de outputs para caché
  const [outputsMap, setOutputsMap] = useState<Record<string, ProcessOutputData[]>>({})
  // Control del dropdown abierto
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  // Loading state para fetch de outputs
  const [loadingOutputs, setLoadingOutputs] = useState<string | null>(null)

  const formatData = (data: any[]): ProcessColumns[] => {
    return data.map((row) => ({
      state: row.state.name,
      clientName: row.clientName,
      name: row.name,
      enrichmentType: row.data.map((d: any) => d.enrichmentType.name).join(', '),
      createdAt: row.createdAt,
      uuid: row.uuid,
    }))
  }

  const downloadS3File = (uuid: string) => {
    downloadFileRequest({ variables: { processUuid: uuid } })
      .then((res) => {
        const data = res.data.downloadFileFromProcess
        download64File(data.encodedFile, data.fileName, data.mimeType)
      })
      .catch(() => {
        triggerToast({ message: 'Hubo un error al descargar el archivo.' })
      })
  }

  const downloadByType = (uuid: string, downloadableTypeNk: string) => {
    downloadOutputRequest({
      variables: { processUuid: uuid, downloadableTypeNk },
    })
      .then((res) => {
        const data = res.data.downloadProcessOutput
        download64File(data.encodedFile, data.fileName, data.mimeType)
      })
      .catch(() => {
        triggerToast({ message: 'Hubo un error al descargar el archivo.' })
      })
  }

  const handleDownloadClick = (uuid: string) => {
    // Si ya tenemos los outputs en caché
    if (outputsMap[uuid]) {
      if (outputsMap[uuid].length <= 1) {
        downloadS3File(uuid)
      }
      // Si hay más de 1, el dropdown se abrirá automáticamente
      return
    }

    // Fetch outputs
    setLoadingOutputs(uuid)
    fetchProcessOutputs({ variables: { processUuid: uuid } })
      .then((res) => {
        const outputs = (res.data?.getProcessOutputs || []).map((o: any) => ({
          id: o.id,
          naturalKey: o.downloadableType.naturalKey,
          name: o.downloadableType.name,
        }))
        setOutputsMap((prev) => ({ ...prev, [uuid]: outputs }))
        setLoadingOutputs(null)
        
        if (outputs.length <= 1) {
          downloadS3File(uuid)
          setOpenDropdown(null)
        }
      })
      .catch(() => {
        setLoadingOutputs(null)
        downloadS3File(uuid)
      })
  }

  const columns: ColumnDef<ProcessColumns>[] = [
    {
      accessorKey: 'state',
      header: 'Estado',
      cell: ({ getValue }) => {
        const state = getValue<string>()
        return (
          <div className="flex items-center space-x-2">
            <StateIndicator state={state} aria-hidden="true" />
            <span className="capitalize">{state.replace('_', ' ')}</span>
          </div>
        )
      },
    },
    { accessorKey: 'clientName', header: 'Nombre cliente' },
    { accessorKey: 'name', header: 'Nombre proceso' },
    { accessorKey: 'enrichmentType', header: 'Tipo de enrichment' },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({ cell }) => format(new Date(cell.getValue() as string), 'dd/MM/yyyy'),
    },
    {
      accessorKey: 'acciones',
      header: 'Acciones',
      cell: ({ row }) => {
        const uuid = row.original.uuid
        const isDisabled = row.original.state !== PROCESS_STATES.COMPLETED
        const isEdit = row.original.state === PROCESS_STATES.CREATED
        const outputs = outputsMap[uuid] || []

        const isLoading = loadingOutputs === uuid
        const isOpen = openDropdown === uuid

        return (
          <div className="flex justify-center items-center gap-2">
            {/* DOWNLOAD DROPDOWN*/}
            <DropdownMenu 
              open={isOpen && outputs.length > 1} 
              onOpenChange={(open) => setOpenDropdown(open ? uuid : null)}
            >
              <Tooltip content={isDisabled ? 'No disponible' : 'Descargar'}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-6 w-6 p-1 flex justify-center items-center disabled:opacity-25 outline-none hover:bg-gray-100 rounded"
                    disabled={isDisabled || isLoading}
                    onClick={() => {
                      setOpenDropdown(uuid)
                      handleDownloadClick(uuid)
                    }}
                  >
                    <Download />
                  </button>
                </DropdownMenuTrigger>
              </Tooltip>

              <DropdownMenuContent align="end" className="w-auto min-w-[max-content] p-1">
                {outputs.map((output) => (
                  <DropdownMenuItem
                    key={output.naturalKey}
                    onClick={() => downloadByType(uuid, output.naturalKey)}
                    className="cursor-pointer"
                  >
                    {output.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* VIEW / EDIT */}
            <Tooltip content={isEdit ? 'Editar' : 'Ver'}>
              <button
                className="h-6 w-6 p-1 flex justify-center items-center hover:bg-gray-100 rounded"
                onClick={() => {
                  const path = isEdit ? '/procesamientos/editar' : '/procesamientos/ver'
                  navigate(path, { state: { uuid } })
                }}
              >
                {isEdit ? <EditIcon /> : <ViewIcon />}
              </button>
            </Tooltip>
          </div>
        )
      },
    },
  ]

  const HeaderCellOptions: TableHeaderCellOptions = {
    state: { orderBy: true, selectSearchFilter: true, title: 'Estado', selectOptions: stateOptions },
    clientName: { orderBy: true, title: 'Nombre cliente' },
    name: { orderBy: false, title: 'Nombre proceso' },
    enrichmentType: { orderBy: true, selectSearchFilter: true, title: 'Tipo de enrichment', selectOptions: enrichmentTypeOptions },
    createdAt: { orderBy: true, title: 'Fecha' },
  }

  return (
    <div className="container max-w-full overflow-hidden">
      <div className="px-4 lg:px-6 xl:px-8">
        <h1 className="title">Procesamientos</h1>
        <div className="subtitle w-full md:w-4/5 lg:w-3/5 xl:w-1/2">
          La tabla a continuación presenta todos los procesamientos realizados.
        </div>
        <PaginatedTable
          query={getProcessesPaginated}
          columns={columns}
          headerCellOptions={HeaderCellOptions}
          formatData={formatData}
          customHeader={<TableHeader setExtraFilters={setExtraFilters} />}
          extraFilters={extraFilters}
        />
      </div>
    </div>
  )
}